import React, { useMemo, useState } from "react";
import Spinner from "../common/Spinner";
import RealtimeAreaMap, { AreaPolygon } from "../common/RealtimeAreaMap";
import { countryFlag, countryLabel } from "./CountryBreakdown";
import { Spark, Delta, TrendInline, trafficTone } from "./TrendBits";
import { trafficOf } from "../../realtime/zoneReadings";
import { MapTotals, MapStamp, TotalRow } from "./MapOverlays";
import type { DashboardGroup } from "../../services/dashboardConfig";
import type { GroupEditing } from "../../hooks/realtime/useDashboardConfig";

export interface RoamingZone {
  id: string;
  name: string;
  intlNow: number; // international (non-domestic) subscribers at the latest sample
  totalNow: number; // everyone present at the latest sample (all countries)
  delta: number; // change vs the comparison sample
  series: number[]; // recent international counts, oldest → newest
  topCountries: { country: string; count: number }[];
  sharePct: number; // international as a share of everyone present
}

/**
 * What a selected zone looks like up close: the figure, its direction, the
 * recent shape, and where the people come from — all readable on the map
 * itself, including in fullscreen.
 */
const ZonePanel: React.FC<{ zone: RoamingZone; intervalLabel: string; onClose: () => void }> = ({
  zone,
  intervalLabel,
  onClose,
}) => {
  const top = zone.topCountries.slice(0, 8);
  const maxC = top[0]?.count || 1;
  return (
    <div className="rounded-xl border border-paper-300/70 bg-surface/95 p-4 shadow-soft backdrop-blur">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Zone</p>
          <p className="truncate font-display text-base font-semibold text-ink" title={zone.name}>
            {zone.name}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-ink-muted hover:bg-paper-100"
        >
          ×
        </button>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">International now</p>
          <p className="font-display text-3xl font-bold tabular-nums text-ink">{zone.intlNow.toLocaleString()}</p>
          <p className="text-[11px] text-ink-faint">{zone.sharePct}% of all present</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Last {intervalLabel}</p>
          <Delta v={zone.delta} />
        </div>
      </div>
      <div className="mt-3">
        <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Trend · last 3h</p>
        <div className="mt-1">
          <Spark values={zone.series} delta={zone.delta} width={272} height={40} />
        </div>
      </div>
      <div className="mt-3">
        <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">Source countries</p>
        <ul className="mt-1.5 space-y-1.5">
          {top.map((c) => (
            <li key={c.country} className="text-xs">
              <div className="flex items-center gap-2">
                <span className="w-5 text-center" aria-hidden>
                  {countryFlag(c.country)}
                </span>
                <span className="flex-1 truncate text-ink">{countryLabel(c.country)}</span>
                <span className="font-medium tabular-nums text-ink">{c.count.toLocaleString()}</span>
              </div>
              <div className="ml-7 mt-0.5 h-1 overflow-hidden rounded bg-paper-100">
                <div className="h-1 rounded bg-brand-500/80" style={{ width: `${(c.count / maxC) * 100}%` }} />
              </div>
            </li>
          ))}
          {!top.length && <li className="text-xs text-ink-faint">No country breakdown for this zone.</li>}
        </ul>
      </div>
    </div>
  );
};

/**
 * Operations board for roaming (international) subscribers: how many are in each
 * monitored zone right now, and which way that's moving. The map leads, tinted
 * by direction, with a pill per zone carrying the actual figures — so colour is
 * reinforcement, never the only encoding. The ranked table sits below.
 */
const RoamingPulse: React.FC<{
  loading: boolean;
  notEnabled: boolean;
  error: string | null;
  zones: RoamingZone[];
  updatedAt: number | null;
  intervalLabel: string;
  /** Polygon WKT per zone id; zones without one are simply absent from the map. */
  wktById: Map<string, string>;
  /** Name for the all-zones total, e.g. the project name → "Total Oslo". */
  title: string;
  /** Named groups of zones totalled separately (e.g. "Sentrum") — the dashboard's config. */
  groups?: DashboardGroup[];
  /** Group editing, owned by the page; when a group is being edited, map clicks toggle membership. */
  groupEditing?: GroupEditing;
  /** Render the board's own map. The project page shows one overall map above the tabs instead. */
  showMap?: boolean;
  }> = ({ loading, notEnabled, error, zones, updatedAt, intervalLabel, wktById, title, groups = [], groupEditing, showMap = true }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const editingGroup = groupEditing?.group ?? null;
  const selected = editingGroup ? null : zones.find((z) => z.id === selectedId) ?? null;
  // Running totals shown on the map: all zones first, then each named subset.
  const totals = useMemo<TotalRow[]>(() => {
    const sum = (zs: RoamingZone[]) => ({
      present: zs.length ? zs.reduce((s, z) => s + z.totalNow, 0) : null,
      intl: zs.reduce((s, z) => s + z.intlNow, 0),
      delta: zs.reduce((s, z) => s + z.delta, 0),
    });
    return [
      { label: `Total ${title}`, ...sum(zones) },
      ...groups.map((g) => {
        const ids = new Set(g.areaIds);
        return { id: g.id, label: `Total ${g.name || "…"}`, ...sum(zones.filter((z) => ids.has(z.id))) };
      }),
    ];
  }, [zones, groups, title]);
  const mapPolygons = useMemo<AreaPolygon[]>(
    () =>
      zones
        .filter((z) => wktById.has(z.id))
        .map((z) => ({
          id: z.id,
          name: z.name,
          wkt: wktById.get(z.id)!,
          color: trafficTone[trafficOf(z.delta, z.intlNow - z.delta)],
          label: (
            <span className="flex items-center gap-1.5">
              <span className="font-medium text-ink">{z.intlNow.toLocaleString()}</span>
              <TrendInline v={z.delta} />
            </span>
          ),
        })),
    [zones, wktById]
  );

  if (loading)
    return (
      <div className="py-16">
        <Spinner />
      </div>
    );
  if (notEnabled)
    return (
      <div className="rounded-2xl border border-warn-line bg-warn-soft p-6 text-sm text-warn-ink">
        The country breakdown isn't enabled for this account. An admin can turn on the{" "}
        <span className="font-mono">realtime_data_country</span> view to see roaming here.
      </div>
    );
  if (error)
    return <div className="rounded-2xl border border-bad-line bg-bad-soft p-6 text-bad-ink">{error}</div>;
  if (!zones.length)
    return (
      <div className="rounded-2xl border border-paper-300/70 bg-surface p-10 text-center text-ink-muted">
        No roaming data for these zones yet.
      </div>
    );

  const totalNow = zones.reduce((s, z) => s + z.intlNow, 0);
  const totalDelta = zones.reduce((s, z) => s + z.delta, 0);
  const maxNow = Math.max(...zones.map((z) => z.intlNow)) || 1;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4 rounded-2xl border border-paper-300/70 bg-surface px-5 py-4 shadow-soft">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
            International subscribers · all zones
          </p>
          <p className="mt-1 font-display text-4xl font-bold text-ink">{totalNow.toLocaleString()}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
            Change · last {intervalLabel}
          </p>
          <p className="mt-1">
            <Delta v={totalDelta} big />
          </p>
        </div>
        <div className="text-right text-xs text-ink-muted">
          <p>
            {zones.length} zone{zones.length === 1 ? "" : "s"} monitored
          </p>
          {updatedAt && <p>updated {new Date(updatedAt).toLocaleTimeString()}</p>}
        </div>
      </div>

      {showMap && mapPolygons.length > 0 && (
        <div className="mb-6 overflow-hidden rounded-2xl border border-paper-300/70 bg-surface shadow-soft">
          <div className="flex items-center justify-between border-b border-paper-200 px-4 py-3">
            <span className="text-sm font-medium text-ink">Zones on the map</span>
            <span className="flex items-center gap-3 text-xs text-ink-faint">
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-ok" />
                rising
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-warn" />
                steady
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-bad" />
                falling
              </span>
              <span>· click a zone for its source countries</span>
            </span>
          </div>
          <div className="h-96 lg:h-[32rem] 2xl:h-[42rem]">
            <RealtimeAreaMap
              polygons={mapPolygons}
              fillOpacity={0.28}
              selectedId={selectedId}
              onSelect={(id) => (editingGroup && groupEditing ? id && groupEditing.onToggleArea(id) : setSelectedId(id))}
              highlightIds={editingGroup ? new Set(editingGroup.areaIds) : null}
              overlayTopLeft={<MapTotals rows={totals} editing={groupEditing} />}
              overlayTopRight={<MapStamp at={updatedAt} />}
              panel={
                selected && (
                  <ZonePanel zone={selected} intervalLabel={intervalLabel} onClose={() => setSelectedId(null)} />
                )
              }
            />
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-paper-300/70 bg-surface">
        <div className="flex items-center justify-between border-b border-paper-200 px-4 py-3">
          <span className="text-sm font-medium text-ink">Zones · ranked by international present</span>
          <span className="text-xs text-ink-faint">change vs {intervalLabel} ago</span>
        </div>
        <div className="divide-y divide-paper-200">
          {zones.map((z) => (
            <div
              key={z.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedId(z.id === selectedId ? null : z.id)}
              onKeyDown={(e) => e.key === "Enter" && setSelectedId(z.id === selectedId ? null : z.id)}
              className={`flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 transition-colors hover:bg-paper-100 ${
                z.id === selectedId ? "bg-brand-50" : ""
              }`}
            >
              <div className="min-w-[180px] flex-1">
                <p className="truncate text-sm font-medium text-ink">{z.name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {z.topCountries.slice(0, 4).map((c) => (
                    <span
                      key={c.country}
                      className="inline-flex items-center gap-1 rounded bg-paper-100 px-1.5 py-0.5 text-[11px] text-ink-muted"
                      title={`${countryLabel(c.country)}: ${c.count.toLocaleString()}`}
                    >
                      <span aria-hidden>{countryFlag(c.country)}</span>
                      {c.count.toLocaleString()}
                    </span>
                  ))}
                </div>
              </div>
              <div className="w-28 text-right">
                <p className="font-display text-2xl font-bold tabular-nums text-ink">
                  {z.intlNow.toLocaleString()}
                </p>
                <p className="text-[11px] text-ink-faint">{z.sharePct}% of all present</p>
              </div>
              <div className="w-28 text-right">
                <Delta v={z.delta} />
              </div>
              <div className="w-24">
                <Spark values={z.series} delta={z.delta} />
              </div>
              <div className="hidden w-24 sm:block">
                <div className="h-1.5 overflow-hidden rounded bg-paper-100">
                  <div className="h-1.5 rounded bg-brand-500/80" style={{ width: `${(z.intlNow / maxNow) * 100}%` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default RoamingPulse;
